import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { Bot, Star, RefreshCw } from '@/components/icons';
import { listRfqs } from '@/api/rfq';
import { rescoreRfq, scoreRfq, type AiScoreResult } from '@/api/ai';
import { formatINRSymbol } from '@/lib/format';
import { apiError } from '@/lib/apiError';

export function AiAnalysisPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [rfqId, setRfqId] = useState<number | ''>('');

  const { data: rfqsResp } = useQuery({
    queryKey: ['rfq', { status: 'Quotes Received' }],
    queryFn: () => listRfqs({ status: 'Quotes Received', pageSize: 200 }),
    staleTime: 30_000,
  });
  const rfqs = useMemo(() => rfqsResp?.data ?? [], [rfqsResp]);

  const scoreQuery = useQuery<AiScoreResult>({
    queryKey: ['ai-score', rfqId],
    queryFn: () => scoreRfq(rfqId as number),
    enabled: !!rfqId,
    staleTime: 30_000,
  });

  const rescoreMutation = useMutation({
    mutationFn: () => rescoreRfq(rfqId as number),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-score', rfqId] });
      qc.invalidateQueries({ queryKey: ['quote-approval'] });
      toast.push('AI scoring updated', 'success');
    },
    onError: (e) => toast.push(apiError(e, 'Re-score failed'), 'error'),
  });

  return (
    <div>
      <SectionHeader
        title="AI Vendor Analysis"      />

      <FilterBar>
        <FilterField label="RFQ">
          <Select
            value={String(rfqId)}
            onChange={(e) => setRfqId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">— Pick an RFQ with received quotes —</option>
            {rfqs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.rfqNo} · {r.items.length} items · {r.quotesReceived ?? 0} quotes
              </option>
            ))}
          </Select>
        </FilterField>
        {/* Merged chip: visible on both viewports, right-aligned inside the filter block. */}
        {rfqId && (
          <button
            type="button"
            onClick={() => rescoreMutation.mutate()}
            disabled={rescoreMutation.isPending}
            className="col-start-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:col-start-auto sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          >
            {rescoreMutation.isPending ? 'Scoring…' : <><Bot size={14} className="mr-1 inline-block align-[-2px]" /> Re-score</>}
          </button>
        )}
      </FilterBar>

      {!rfqId && (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          Select an RFQ to view AI-scored vendor comparison
        </div>
      )}

      {rfqId && scoreQuery.isLoading && (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          Loading scores…
        </div>
      )}

      {scoreQuery.data && (
        <>
          <div className="mb-4 rounded-card border border-accent/30 bg-gradient-to-br from-accent/10 to-transparent p-5 text-center">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">
              AI Recommended Total Savings
            </div>
            <div className="mt-1 text-4xl font-black text-accent">
              {formatINRSymbol(scoreQuery.data.totalSavings)}
            </div>
            <div className="mt-1 text-xs text-[var(--text-muted)]">
              vs picking the highest-priced quote per item
            </div>
            <div className="mt-2 inline-block rounded-full border px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={
                scoreQuery.data.aiPowered
                  ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
                  : { borderColor: 'var(--border-color)', color: 'var(--text-muted)' }
              }
            >
              {scoreQuery.data.aiPowered ? <><Bot size={12} className="mr-1 inline-block align-[-2px]" /> {scoreQuery.data.model}</> : 'Deterministic fallback'}
            </div>
          </div>

          {scoreQuery.data.advisorNotes.length > 0 && (
            <div className="mb-4 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-5">
              <div className="mb-3 text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">
                AI Recommendations
              </div>
              <ul className="space-y-2">
                {scoreQuery.data.advisorNotes.map((note, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--text-secondary)]">
                    <span className="text-accent">▸</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-4">
            {scoreQuery.data.results.map((item) => {
              const best = item.results.find((q) => q.isBest);
              const alt = item.results.find((q) => q.isAlternative);
              return (
              <div
                key={item.rfqItemId}
                className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl"
              >
                {/* Item header */}
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-base font-extrabold text-[var(--text-primary)]">{item.itemName}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-muted)]">
                      {item.targetPrice != null && (
                        <span>Target: <strong className="text-[var(--text-primary)]">{formatINRSymbol(item.targetPrice)}</strong></span>
                      )}
                      {item.targetPrice != null && <span>·</span>}
                      <span>Best savings: <strong className="text-accent">{formatINRSymbol(item.savingsVsHighest)}</strong></span>
                      {item.vsTarget != null && (
                        <>
                          <span>·</span>
                          <span
                            className="rounded-full px-1.5 py-0.5 text-[10px] font-black"
                            style={item.vsTarget <= 0
                              ? { background: 'rgb(34 197 94 / 0.12)', color: '#22c55e' }
                              : { background: 'rgb(239 68 68 / 0.12)', color: '#ef4444' }}
                          >
                            Best quote {item.vsTarget <= 0 ? `${Math.abs(item.vsTarget)}% below` : `${item.vsTarget}% above`} target
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* AI recommendation */}
                {item.recommendation && (
                  <div className="mb-3 rounded-md border border-accent/20 bg-accent/[0.04] px-3 py-2 text-xs text-[var(--text-secondary)]">
                    <span className="font-bold text-accent"><Bot size={12} className="mr-1 inline-block align-[-2px]" /> AI:</span> {item.recommendation}
                  </div>
                )}

                {/* Best + Alternative callout cards */}
                {(best || alt) && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {best && (
                      <div className="flex-1 min-w-[180px] rounded-lg border border-success/30 bg-success/[0.07] px-3 py-2">
                        <div className="text-[10px] font-black uppercase tracking-wide text-success"><Star size={11} className="mr-1 inline-block align-[-1px]" /> Best Vendor</div>
                        <div className="mt-0.5 font-bold text-[var(--text-primary)]">{best.vendor}</div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {formatINRSymbol(best.unitPrice)} · {best.deliveryDays ?? '—'}d ·{' '}
                          score <span className="font-bold text-success">{best.aiScore.toFixed(1)}</span>
                          {best.vsTarget != null && (
                            <span className={best.vsTarget <= 0 ? ' text-success' : ' text-danger'}>
                              {' '}({best.vsTarget <= 0 ? `${Math.abs(best.vsTarget)}% below` : `${best.vsTarget}% above`} target)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {alt && (
                      <div className="flex-1 min-w-[180px] rounded-lg border border-accent/20 bg-accent/[0.05] px-3 py-2">
                        <div className="text-[10px] font-black uppercase tracking-wide text-accent">⟳ Alternative</div>
                        <div className="mt-0.5 font-bold text-[var(--text-primary)]">{alt.vendor}</div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {formatINRSymbol(alt.unitPrice)} · {alt.deliveryDays ?? '—'}d ·{' '}
                          score <span className="font-bold text-accent">{alt.aiScore.toFixed(1)}</span>
                          {alt.vsTarget != null && (
                            <span className={alt.vsTarget <= 0 ? ' text-success' : ' text-danger'}>
                              {' '}({alt.vsTarget <= 0 ? `${Math.abs(alt.vsTarget)}% below` : `${alt.vsTarget}% above`} target)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {item.results.length === 0 ? (
                  <div className="rounded-md border border-[var(--border-color)] bg-white/[0.02] p-3 text-center text-xs text-[var(--text-muted)]">
                    No quotes received for this item yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-accent/[0.05] text-[10px] font-black uppercase tracking-wide text-accent">
                          <th className="px-3 py-1.5 text-left">Vendor</th>
                          <th className="px-3 py-1.5 text-right">Unit Price</th>
                          {item.targetPrice != null && <th className="px-3 py-1.5 text-right">vs Target</th>}
                          <th className="px-3 py-1.5 text-right">Delivery</th>
                          <th className="px-3 py-1.5 text-right">Rating</th>
                          <th className="px-3 py-1.5 text-right">On-time %</th>
                          <th className="px-3 py-1.5 text-right">AI Score</th>
                          <th className="px-3 py-1.5 text-center">Rank</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.results.map((q) => (
                          <tr
                            key={q.quoteItemId}
                            className={q.isBest ? 'bg-success/[0.08]' : q.isAlternative ? 'bg-accent/[0.05]' : 'border-b border-white/[0.04]'}
                          >
                            <td className="px-3 py-1.5 font-bold">{q.vendor}</td>
                            <td className="px-3 py-1.5 text-right">{formatINRSymbol(q.unitPrice)}</td>
                            {item.targetPrice != null && (
                              <td className="px-3 py-1.5 text-right">
                                {q.vsTarget != null ? (
                                  <span
                                    className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                                    style={q.vsTarget <= 0
                                      ? { background: 'rgb(34 197 94 / 0.12)', color: '#22c55e' }
                                      : { background: 'rgb(239 68 68 / 0.12)', color: '#ef4444' }}
                                  >
                                    {q.vsTarget > 0 ? '+' : ''}{q.vsTarget.toFixed(1)}%
                                  </span>
                                ) : '—'}
                              </td>
                            )}
                            <td className="px-3 py-1.5 text-right">{q.deliveryDays ?? '—'} d</td>
                            <td className="px-3 py-1.5 text-right">{q.rating?.toFixed(1) ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right">{q.ontimePct?.toFixed(0) ?? '—'}%</td>
                            <td className="px-3 py-1.5 text-right">
                              <span className="font-extrabold text-accent">{q.aiScore.toFixed(1)}</span>
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              {q.isBest ? <span className="text-success font-black"><Star size={11} className="mr-1 inline-block align-[-1px]" /> Best</span> : q.isAlternative ? <span className="text-accent font-bold"><RefreshCw size={11} className="mr-1 inline-block align-[-1px]" /> Alt</span> : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
